import { format, parseISO } from 'date-fns';
import moment from 'moment-timezone';

import type { EstimatedBucketVolume, UnitHourlyUsage, UnitUsage } from '../Usage/types';
import { addCreate, naturalCompare } from '../util';

type CSVMetadata = { start: number | string; end: number | string; timezone: string };

type EstimatedByBucket = { [bucketKey: string]: { volume: number; reasons: string[] } };

/**
 * Aggregate estimated buckets by a row key (local date string for the daily CSV,
 * epoch-second string for the hourly CSV). Same-bucket estimates sum their volumes
 * and collect distinct reasons.
 */
const aggregateEstimates = (
  entries: EstimatedBucketVolume[],
  bucketKey: (entry: EstimatedBucketVolume) => string,
): EstimatedByBucket => {
  const aggregated: EstimatedByBucket = {};
  entries.forEach((entry) => {
    const key = bucketKey(entry);
    if (!aggregated[key]) {
      aggregated[key] = { volume: 0, reasons: [] };
    }
    aggregated[key].volume += entry.volume;
    if (!aggregated[key].reasons.includes(entry.reason)) {
      aggregated[key].reasons.push(entry.reason);
    }
  });
  return aggregated;
};

/** The two estimated cells for one row: volume and (quoted, CSV-escaped) reason. */
const estimatedCells = (aggregated: EstimatedByBucket, key: string): string => {
  const entry = aggregated[key];
  const volume = (entry?.volume ?? 0).toFixed(2);
  const reason = entry?.reasons.length ? `"${entry.reasons.join('; ').replace(/"/g, '""')}"` : '';
  return `${volume},${reason}`;
};

export const generateUsageCSV = (
  rawUsage: UnitUsage | undefined,
  metadata: CSVMetadata,
): string => {
  if (!rawUsage) {
    return '';
  }

  const usageByDate = {
    TOTAL: {},
    ...rawUsage?.devices.reduce((acc, cur) => ({ ...acc, [cur.name || cur.id]: {} }), {}),
  };

  rawUsage?.devices.forEach((device) => {
    device.daily_usages.forEach((usageData) => {
      // Use volume directly from backend (already rounded to 2 decimals)
      // DO NOT apply Math.round() here; it causes double-rounding errors
      addCreate(usageByDate[device.name || device.id], usageData.date, usageData.volume);
      addCreate(usageByDate.TOTAL, usageData.date, usageData.volume);
    });
  });

  // Estimated usage keyed by local date; columns appear only when estimates exist so
  // the CSV stays bit-for-bit identical for customers without estimates
  const hasEstimates = rawUsage.estimated_bucket_volumes.length > 0;
  const estimatedByDate = aggregateEstimates(rawUsage.estimated_bucket_volumes, (entry) =>
    moment.tz(entry.bucket_start * 1000, metadata.timezone).format('YYYY-MM-DD'),
  );

  const estimatedHeaderColumns = hasEstimates ? ',Estimated (gal),Estimated Reason' : '';

  const headers = `Date (${metadata.timezone}),Unit Total (gal),"${Object.keys(usageByDate)
    .filter((k) => k !== 'TOTAL')
    .sort(naturalCompare)
    .join(' (gal)","')} (gal)"${estimatedHeaderColumns},,start: ${metadata.start},end: ${
    metadata.end
  },timezone: ${metadata.timezone}\n`;

  // Rows cover every date with real usage plus estimate-only dates (a unit with no
  // devices can still carry estimates)
  const rowDates = Array.from(
    new Set([...Object.keys(usageByDate.TOTAL), ...Object.keys(estimatedByDate)]),
  ).sort();

  const estimatedTotalCells = hasEstimates
    ? `,${Object.values(estimatedByDate)
        .reduce((acc, cur) => acc + cur.volume, 0)
        .toFixed(2)},`
    : '';

  const totalsRow =
    rowDates.length &&
    `Total ${format(parseISO(rowDates[0]), 'M/d/yyyy')} - ${format(
      parseISO(rowDates[rowDates.length - 1]),
      'M/d/yyyy',
    )},${(Object.values(usageByDate.TOTAL) as number[])
      .reduce((acc, cur) => acc + cur, 0)
      .toFixed(2)},${Object.keys(usageByDate)
      .filter((deviceId) => deviceId !== 'TOTAL')
      .sort(naturalCompare)
      .map((deviceId) =>
        // @ts-ignore
        Object.values(usageByDate[deviceId])
          // @ts-ignore
          .reduce((acc, cur) => acc + cur, 0)
          .toFixed(2),
      )
      .join(',')}${estimatedTotalCells}\n`;

  const body = rowDates
    .map((dateStr) => {
      const estimatedRowCells = hasEstimates ? `,${estimatedCells(estimatedByDate, dateStr)}` : '';
      return `${format(parseISO(dateStr), 'M/d/yyyy')},${Number(
        usageByDate.TOTAL[dateStr] ?? 0,
      ).toFixed(2)},${Object.keys(usageByDate)
        .filter((k) => k !== 'TOTAL')
        .sort(naturalCompare)
        .map((k) =>
          !isNaN(usageByDate[k][dateStr]) ? Number(usageByDate[k][dateStr]).toFixed(2) : '0.00',
        )
        .join(',')}${estimatedRowCells}`;
    })
    .join('\n');

  return `${headers}${totalsRow || ''}${body}`;
};

/**
 * CSV variant for hourly usage. Emits one row per hour (vs one row per day).
 * Daily generateUsageCSV is left untouched bit-for-bit so its output stays stable
 * for users without the HOURLY_USAGE flag.
 */
export const generateHourlyUsageCSV = (
  rawUsage: UnitHourlyUsage | undefined,
  metadata: CSVMetadata,
): string => {
  if (!rawUsage) {
    return '';
  }

  // Bucket key is the epoch_second integer (start of hour). Sorting numerically by parsing back.
  const usageByHour: { TOTAL: { [hour: string]: number } } & {
    [deviceName: string]: { [hour: string]: number };
  } = {
    TOTAL: {},
    ...rawUsage.devices.reduce((acc, cur) => ({ ...acc, [cur.name || cur.id]: {} }), {}),
  };

  rawUsage.devices.forEach((device) => {
    device.hourly_usages.forEach((bucket) => {
      const hourKey = String(bucket.time);
      addCreate(usageByHour[device.name || device.id], hourKey, bucket.volume);
      addCreate(usageByHour.TOTAL, hourKey, bucket.volume);
    });
  });

  // Estimated usage keyed by the hour bucket's epoch_second; columns appear only when
  // estimates exist, matching the daily CSV behavior
  const hasEstimates = rawUsage.estimated_bucket_volumes.length > 0;
  const estimatedByHour = aggregateEstimates(rawUsage.estimated_bucket_volumes, (entry) =>
    String(entry.bucket_start),
  );

  const estimatedHeaderColumns = hasEstimates ? ',Estimated (gal),Estimated Reason' : '';

  const headers = `Hour (${metadata.timezone}),Unit Total (gal),"${Object.keys(usageByHour)
    .filter((k) => k !== 'TOTAL')
    .sort(naturalCompare)
    .join(' (gal)","')} (gal)"${estimatedHeaderColumns},,start: ${metadata.start},end: ${
    metadata.end
  },timezone: ${metadata.timezone}\n`;

  const sortedHours = Array.from(
    new Set([...Object.keys(usageByHour.TOTAL), ...Object.keys(estimatedByHour)]),
  ).sort((a, b) => Number(a) - Number(b));

  // Format timestamps in the property's timezone (not the browser's local timezone) so the CSV
  // matches what the customer sees in the webapp regardless of where they're physically located.
  const formatHour = (epochSecond: number): string =>
    moment.tz(epochSecond * 1000, metadata.timezone).format('M/D/YYYY h:mm A');

  const estimatedTotalCells = hasEstimates
    ? `,${Object.values(estimatedByHour)
        .reduce((acc, cur) => acc + cur.volume, 0)
        .toFixed(2)},`
    : '';

  const totalsRow =
    sortedHours.length &&
    `Total ${formatHour(Number(sortedHours[0]))} - ${formatHour(
      Number(sortedHours[sortedHours.length - 1]),
    )},${(Object.values(usageByHour.TOTAL) as number[])
      .reduce((acc, cur) => acc + cur, 0)
      .toFixed(2)},${Object.keys(usageByHour)
      .filter((deviceId) => deviceId !== 'TOTAL')
      .sort(naturalCompare)
      .map((deviceId) =>
        (Object.values(usageByHour[deviceId]) as number[])
          .reduce((acc, cur) => acc + cur, 0)
          .toFixed(2),
      )
      .join(',')}${estimatedTotalCells}\n`;

  const body = sortedHours
    .map((hourKey) => {
      const estimatedRowCells = hasEstimates ? `,${estimatedCells(estimatedByHour, hourKey)}` : '';
      return `${formatHour(Number(hourKey))},${Number(usageByHour.TOTAL[hourKey] ?? 0).toFixed(
        2,
      )},${Object.keys(usageByHour)
        .filter((k) => k !== 'TOTAL')
        .sort(naturalCompare)
        .map((k) =>
          !isNaN(usageByHour[k][hourKey]) ? Number(usageByHour[k][hourKey]).toFixed(2) : '0.00',
        )
        .join(',')}${estimatedRowCells}`;
    })
    .join('\n');

  return `${headers}${totalsRow || ''}${body}`;
};
